/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React, {
  ChangeEvent,
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState
} from 'react';
import {useDispatch} from 'react-redux';
import {ColDef} from 'ag-grid-community';
import debounce from 'lodash.debounce';
import {translate} from '@ion/core/src/locale/locale';
import {
  AsyncTaskStatus,
  CarbRequestType,
  CTCEntities,
  JobStatus
} from '@ion/core/src/types/entities';
import {clone} from '@ion/core/src/utils/ObjectUtil';
import {isEqual} from '@ion/core/src/utils/StringUtil';
import {CSVExportButton} from '@ion/dls/src/components/buttons/CSVExportButton';
import {IconButtonLabelPlacement} from '@ion/dls/src/components/buttons/IconButton';
import {RefreshGridButton} from '@ion/dls/src/components/buttons/RefreshGridButton';
import {GridControlsBank} from '@ion/dls/src/components/grid/GridControlsBank';
import {SearchField} from '@ion/dls/src/components/search/SearchField';
import {SnackbarMessage} from '@ion/dls/src/components/snackbars/SnackbarMessage';
import useSnackbar from '@ion/dls/src/components/snackbars/useSnackbar';
import {JobStatusChooser} from '@ion/dls/src/editors/JobStatusChooser';
import {EmailExportRequestDialog} from '@ion/dls/src/export/EmailExportRequestDialog';
import {
  filterColumns,
  resolveDefinitions
} from '@ion/dls/src/grid/ColumnResolver';
import {IonGridAPI} from '@ion/dls/src/grid/IonGrid';
import IonInfiniteScrollGrid from '@ion/dls/src/grid/IonInfiniteScrollGrid';
import {useExtendStyles} from '@ion/dls/src/hooks/useExtendStyles';
import {DLSTheme} from '@ion/dls/src/types/types';
import {exportTaskStatus} from '../../store/actions/exporter';
import JobStatusCellRenderer from './JobStatusCellRenderer';
import {TaskStatusGridEmailProps} from './JobStatusDrawer';
import {
  CACHE_BLOCK_SIZE,
  getDataSource,
  getExportTotalCount,
  getStatusGridExcludedColumns,
  hasFailedJobStatus,
  isReplaceGroupSubtask,
  TaskStatusGridTooltip
} from './TaskStatusGridHelpers';

export interface TaskStatusGridProps extends TaskStatusGridEmailProps {
  jobStatus?: JobStatus;
  enableSearch?: boolean;
}

export interface getColumnDefinitionsTypes {
  excludedColumns: string[];
  filterableColumns: string[];
  isReplaceGroup: boolean;
  isFailed: boolean;
  isPartial: boolean;
}

// styles that get passed to the SearchField component
const searchFieldStyles = () => ({
  searchFieldRoot : {
    height : '55px'
  },
  searchFieldInput : {
    fontSize : '16px'
  }
});

// Styles for the Report Container
const useStyles = (theme: DLSTheme) => ({
  searchField : {
    width                          : '40%',
    [theme.breakpoints.down('sm')] : {
      marginRight : '0px',
      width       : '100%'
    }
  },
  filterSection : {
    display : 'flex',
    padding : '7px 1px'
  },
  jobStatus : {
    width : '30%'
  },
  exportIcon : {
    display        : 'flex',
    width          : ({enableSearch}) => enableSearch ? '30%' : '100%',
    justifyContent : ({enableSearch}) => enableSearch ? 'start' : 'end'

  },
  taskInnerClass : {
    padding : '0px 10px'
  },
  bulkTaskGridStyle : {
    '& .ag-center-cols-container' : {
      minWidth : '100%'
    },
    '& .ag-cell-wrapper' : {
      height : '100%'
    },
    '.custom-cell' : {
      overflow     : 'hidden !important',
      textOverflow : 'ellipsis !important'
    }
  }
});

const allLabel = 'ALL';

/**
 * Grid to be rendered on the Admin Devices page.
 */
export const TaskStatusGrid = ({
  jobStatus: mainTask,
  enableSearch = false,
  hasEmailExport = false
}: TaskStatusGridProps, ref) => {
  const dispatch                            = useDispatch();
  const classes                             = useExtendStyles(useStyles, searchFieldStyles)({enableSearch});
  const taskRef                             = useRef<IonGridAPI>();
  const [searchValue, setSearchValue]       = useState('');
  const [selectedStatus, setSelectedStatus] = useState(allLabel);
  const [gridApi, setGridApi]               = useState(null);
  const [subTasks, setSubTasks]             = useState([]);
  const [rowData, setRowData]               = useState<CTCEntities>([]);
  const [hasTaskRequest, setHasTaskRequest] = useState<boolean>(false);
  const requestType                         = mainTask.other?.requestType;
  const isBulkOperation                     = !requestType;
  const mainTaskId                          = mainTask.id;
  const entityType                          = mainTask?.other?.entityType;
  const actionType                          = mainTask?.other?.bulkOperation?.actionType;
  const taskStatus                          = mainTask?.status;
  const exportTotalCount                    = getExportTotalCount(mainTask);
  const hasRowData                          = rowData?.length === 0;

  const excludedColumns = getStatusGridExcludedColumns(requestType || entityType, actionType, taskStatus);

  const hasFailedStatus = useMemo(() => {
    return hasFailedJobStatus(rowData, mainTask?.status);
  }, [rowData, mainTask]);

  const {
    isBulkExport,
    isGroupAssignment,
    isReplaceGroup
  } = useMemo(() => {
    const name = mainTask?.name;
    return {
      isBulkExport      : !!name?.includes('exportEntities'),
      isGroupAssignment : !!name?.includes('assignGroup'),
      isReplaceGroup    : !!isReplaceGroupSubtask(mainTask?.other)
    };
  }, [mainTask]);

  const columnDefinitions = useMemo(() =>
    getColumnDefinitions({
      excludedColumns,
      isReplaceGroup,
      filterableColumns : [],
      isFailed          : hasFailedStatus,
      isPartial         : isEqual(mainTask?.status, AsyncTaskStatus.PARTIAL_SUCCESS)
    })
  , [excludedColumns, isGroupAssignment, mainTask]);

  useEffect(() => {
    if (subTasks?.length) {
      handleSearch(subTasks);
    }
  }, [subTasks, searchValue, selectedStatus]);

  /**
   * Returns a reference to the grids public api ..
   */
  useImperativeHandle(ref, () => ({
    onExport,
    getExportColumnDefinitions
  }));

  const handleSearch = useCallback((data) => {
    let searchList = [];
    if (searchValue === '') {
      if (selectedStatus === allLabel) {
        searchList = clone(data);
      } else {
        searchList = data.filter(task => {
          return task.status === selectedStatus;
        });
      }
    } else if (searchValue !== '') {
      searchList = data.filter(task => {
        let {esn, name, status} = task;
        esn = esn?.toUpperCase() || '';
        name = name?.toUpperCase() || '';
        if (selectedStatus === allLabel) {
          return esn?.includes(searchValue) || name?.includes(searchValue);
        }
        return ((esn?.includes(searchValue) || name?.includes(searchValue)) && status === selectedStatus);
      });
    }
    setRowData(searchList);
  }, [searchValue, selectedStatus]);

  const getExportColumnDefinitions = useCallback(() => {
    const ionGridData = taskRef?.current?.getExportParams() || {};
    const {columns}   = ionGridData || {};
    if (columns) {
      const definitions = {};
      columns.forEach(({headerName, field}) => {
        if (headerName) {
          const fieldLabel = attributeMappings[field] || field;
          definitions[fieldLabel] = headerName;
        }
      });
      return {columns: definitions};
    }
    return {columns: {}};
  }, []);

  const onExport = function() {
    if (hasEmailExport) {
      setHasTaskRequest(true);
    } else {
      const params = taskRef.current?.getExportParams(false, false);
      dispatch(exportTaskStatus(params, rowData));
    }
  };

  function onSearchChange(e: ChangeEvent<{name?: string; value: string;}>) {
    e.persist();
    debounceFieldChange(e.target.value);
  }

  const debounceFieldChange = debounce((value: string) => {
    setSearchValue(value);
  }, 300);

  function onStatusChange(value, _label) {
    setSelectedStatus(value);
  }

  const onGridReady = useCallback((api) => {
    api.sizeColumnsToFit();
    const datasource = getDataSource({
      api,
      mainTaskId,
      isBulkOperation,
      isBulkExport,
      requestType,
      setRowData
    });
    api.setGridOption('serverSideDatasource', datasource);
    setGridApi(api);
  }, [isBulkExport, isBulkOperation]);

  const onRefresh = useCallback(function(_e): void {
    const datasource = getDataSource({
      api : gridApi,
      mainTaskId,
      isBulkOperation,
      isBulkExport,
      requestType,
      setRowData
    });
    gridApi.setGridOption('serverSideDatasource', datasource);
  }, [gridApi, isBulkExport, isBulkOperation]);

  const attributeMappings = {
    name           : 'entityName',
    identifier     : 'identifier',
    identifierType : 'identifierType',
    status         : 'status',
    features       : 'features',
    lastModifiedOn : 'lastModifiedOn',
    errorCode      : 'code',
    reason         : 'reason'
  };

  const onCloseEmailExportDialog = useCallback(() => {
    setHasTaskRequest(false);
  }, []);

  const {
    show: showBulkSnackbar,
    message: bulkSnackbarMessage,
    openSnackbar: onComplete,
    dismissSnackbar: dismissBulkSnackbar
  } = useSnackbar();

  const exportContext = CarbRequestType[requestType?.toUpperCase()] || entityType || requestType;

  return (
    <>
      <div className={classes.filterSection}>
        {enableSearch && (
          <>
            <div className={classes.searchField}>
              <SearchField placeholder={translate('jobstatus:searchPlaceHolder')}
                overrides={searchFieldStyles}
                onChange={onSearchChange} />
            </div>
            <div className={classes.jobStatus}>
              <JobStatusChooser classes={classes.taskInnerClass}
                status={selectedStatus}
                onChange={onStatusChange} />
            </div>
          </>
        )}
        <GridControlsBank controlsClassName={classes.exportIcon}>
          <CSVExportButton onExport={onExport}
            disableExport={hasEmailExport}
            enableExport={hasRowData}
            hasNoRowSelection={hasEmailExport}
            exportLimit={hasEmailExport}
            currentLabelPlacement={IconButtonLabelPlacement.HORIZONTAL} />
          <RefreshGridButton onRefresh={onRefresh}
            currentLabelPlacement={IconButtonLabelPlacement.HORIZONTAL} />
        </GridControlsBank>
      </div>
      <IonInfiniteScrollGrid ref={taskRef}
        autoGroupColumnDef={{
          headerName                : translate('jobstatus:grid.columns.name'),
          field                     : 'name',
          suppressMenu              : true,
          suppressHeaderMenuButton  : true,
          suppressHeaderContextMenu : true
        }}
        customGridClass={classes.bulkTaskGridStyle}
        augmenter={taskStatusAugmenter}
        onGridReady={onGridReady}
        cacheBlockSize={CACHE_BLOCK_SIZE}
        columnDefinitions={columnDefinitions}
        isEnableCellTextSelection={true}
        columnComponents={{
          statusCellRenderer : item => (
            <JobStatusCellRenderer value={item.value} isPadding />
          )
        }} />
      {hasTaskRequest && (
        <EmailExportRequestDialog exportCount={exportTotalCount}
          show={hasTaskRequest}
          exportContext={exportContext}
          taskId={mainTaskId}
          isTaskStatus={hasEmailExport}
          getExportColumnDefinitions={getExportColumnDefinitions}
          showSnackbar={onComplete}
          onClose={onCloseEmailExportDialog} />
      )}
      <SnackbarMessage show={showBulkSnackbar}
        message={bulkSnackbarMessage}
        onDismiss={dismissBulkSnackbar} />
    </>
  );
};

/**
 * Given a list of rows, checks for any subresults (such as in 'Replace Group' operation)
 * to then add to the exported CSV file.
 *
 * @param rows grid rows to be augmented
 * @returns final rows to be exported (including any subresults)
 */
const taskStatusAugmenter = (rows) => {
  return rows.flatMap(row => [row, ...(row?.subResults ?? [])]);
};

/**
 *
 * Returns column definitions for the desktop grid implementation ...
 */
const getColumnDefinitionDesktop = ({
  excludedColumns,
  filterableColumns,
  isReplaceGroup,
  isFailed,
  isPartial
}: getColumnDefinitionsTypes): ColDef[] => {
  const hasExcludedColumns = excludedColumns.includes('identifierType');
  const entityNameColumn = {
    headerName   : translate('jobstatus:grid.columns.name'),
    field        : 'name',
    sortable     : false,
    filter       : false,
    resizable    : true,
    unSortIcon   : false,
    hide         : isReplaceGroup,
    rowGroup     : isReplaceGroup,
    cellRenderer : null
  };
  const columnDefinitions = [{
    headerName : translate('jobstatus:grid.columns.identifierType'),
    field      : 'identifierType',
    width      : 140,
    sortable   : false,
    filter     : false,
    resizable  : true,
    unSortIcon : false,
    hide       : false
  }, {
    headerName : translate('jobstatus:grid.columns.identifier'),
    field      : 'identifier',
    sortable   : false,
    filter     : false,
    resizable  : true,
    unSortIcon : false,
    hide       : false
  }];

  if (hasExcludedColumns) {
    columnDefinitions.push(entityNameColumn);
  }
  columnDefinitions.push({
    headerName   : translate('jobstatus:grid.columns.status'),
    field        : 'status',
    minWidth     : 130,
    cellRenderer : 'statusCellRenderer',
    sortable     : false,
    filter       : false,
    resizable    : true,
    unSortIcon   : false,
    hide         : false
  });

  if (isFailed || isPartial) {
    columnDefinitions.push(
      {
        headerName : translate('jobstatus:grid.columns.errorCode'),
        field      : 'errorCode',
        sortable   : false,
        filter     : false,
        resizable  : true,
        unSortIcon : false,
        hide       : false
      }, {
        headerName   : translate('jobstatus:grid.columns.reason'),
        field        : 'reason',
        sortable     : false,
        filter       : false,
        resizable    : true,
        unSortIcon   : false,
        hide         : false,
        rowGroup     : false,
        cellClass    : 'custom-cell',
        cellRenderer : TaskStatusGridTooltip
      });
  }
  columnDefinitions.push(
    {
      headerName : translate('jobstatus:grid.columns.lastModifiedOn'),
      field      : 'lastModifiedOn',
      width      : 180,
      sortable   : false,
      filter     : false,
      resizable  : true,
      unSortIcon : false,
      hide       : false
    }, {
      headerName   : translate('jobstatus:grid.columns.features'),
      field        : 'features',
      sortable     : false,
      filter       : false,
      resizable    : true,
      unSortIcon   : false,
      hide         : false,
      cellClass    : 'custom-cell',
      cellRenderer : TaskStatusGridTooltip
    });
  if (!hasExcludedColumns) {
    columnDefinitions.push(entityNameColumn);
  }
  return filterColumns({
    columns : columnDefinitions,
    excludedColumns,
    filterableColumns
  });
};

/**
 *
 * Returns column definitions for mobile grid implementation ...
 */
const getColumnDefinitionMobile = ({
  excludedColumns,
  filterableColumns,
  isReplaceGroup,
  isFailed,
  isPartial
}: getColumnDefinitionsTypes): ColDef[] => {
  const hasExcludedColumns = excludedColumns.includes('identifierType');
  const entityNameColumn = {
    headerName : translate('jobstatus:grid.columns.name'),
    field      : 'name',
    sortable   : false,
    filter     : false,
    resizable  : true,
    unSortIcon : false,
    hide       : isReplaceGroup,
    rowGroup   : isReplaceGroup
  };
  const columnDefinitions = [{
    headerName : translate('jobstatus:grid.columns.identifierType'),
    field      : 'identifierType',
    sortable   : false,
    filter     : false,
    resizable  : true,
    unSortIcon : false,
    hide       : false
  }, {
    headerName : translate('jobstatus:grid.columns.identifier'),
    field      : 'identifier',
    sortable   : false,
    filter     : false,
    resizable  : true,
    unSortIcon : false,
    hide       : false
  }];
  if (hasExcludedColumns) {
    columnDefinitions.push(entityNameColumn);
  }
  columnDefinitions.push({
    headerName   : translate('jobstatus:grid.columns.status'),
    field        : 'status',
    cellRenderer : 'statusCellRenderer',
    sortable     : false,
    filter       : false,
    resizable    : true,
    unSortIcon   : false,
    hide         : false
  });
  if (isFailed || isPartial) {
    columnDefinitions.push(
      {
        headerName : translate('jobstatus:grid.columns.errorCode'),
        field      : 'errorCode',
        sortable   : false,
        filter     : false,
        resizable  : true,
        unSortIcon : false,
        hide       : false
      }, {
        headerName : translate('jobstatus:grid.columns.reason'),
        field      : 'reason',
        sortable   : false,
        filter     : false,
        resizable  : true,
        unSortIcon : false,
        hide       : false
      });
  }
  columnDefinitions.push({
    headerName : translate('jobstatus:grid.columns.lastModifiedOn'),
    field      : 'lastModifiedOn',
    sortable   : false,
    filter     : false,
    resizable  : true,
    unSortIcon : false,
    hide       : false
  }, {
    headerName : translate('jobstatus:grid.columns.features'),
    field      : 'features',
    sortable   : false,
    filter     : false,
    resizable  : true,
    unSortIcon : false,
    hide       : false
  });
  if (!hasExcludedColumns) {
    columnDefinitions.push(entityNameColumn);
  }
  return filterColumns({
    columns : columnDefinitions,
    excludedColumns,
    filterableColumns
  });
};

const getColumnDefinitions = (
  {excludedColumns, filterableColumns, isReplaceGroup, isFailed, isPartial}: getColumnDefinitionsTypes
) => ({
  desktop : resolveDefinitions(
    getColumnDefinitionDesktop({excludedColumns, filterableColumns, isReplaceGroup, isFailed, isPartial})
  ),
  mobile : resolveDefinitions(
    getColumnDefinitionMobile({excludedColumns, filterableColumns, isReplaceGroup, isFailed, isPartial})
  )
});

export default forwardRef(TaskStatusGrid);
